import { useState } from "react";
import { Link } from "react-router";
import { useFetch } from "../../hooks/useFetch";

type ChangelogEntry = {
	id: string;
	title: string;
	slug: string;
	body: string;
	version: string | null;
	publishedAt: string | null;
	category: string;
	createdAt: string;
	updatedAt: string;
};

type ListResponse = {
	data: ChangelogEntry[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
};

const categoryBadge: Record<string, string> = {
	feature: "bg-blue-100 text-blue-700",
	fix: "bg-red-100 text-red-700",
	improvement: "bg-yellow-100 text-yellow-700",
};

export function AdminChangelogListPage() {
	const [page, setPage] = useState(1);
	const { data, loading, error, refetch } = useFetch<ListResponse>(
		`/api/admin/changelog?page=${page}&pageSize=20`,
	);
	const [deleting, setDeleting] = useState<string | null>(null);

	async function handleDelete(entry: ChangelogEntry) {
		if (!window.confirm(`Delete "${entry.title}"?`)) return;
		setDeleting(entry.id);
		try {
			await fetch(`/api/admin/changelog/${entry.id}`, {
				method: "DELETE",
				credentials: "include",
			});
			refetch();
		} finally {
			setDeleting(null);
		}
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-4">
				<h1 className="text-2xl font-bold text-gray-900">Changelog</h1>
				<Link
					to="/admin/changelog/new"
					className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
				>
					New Entry
				</Link>
			</div>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{loading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

			{data && (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2">Title</th>
									<th className="px-4 py-2">Category</th>
									<th className="px-4 py-2">Status</th>
									<th className="px-4 py-2">Version</th>
									<th className="px-4 py-2">Date</th>
									<th className="px-4 py-2">Actions</th>
								</tr>
							</thead>
							<tbody>
								{data.data.map((entry) => (
									<tr key={entry.id} className="border-b border-gray-200">
										<td className="px-4 py-2 font-medium">
											<Link
												to={`/admin/changelog/${entry.slug}/edit`}
												className="text-blue-600 hover:underline"
											>
												{entry.title}
											</Link>
										</td>
										<td className="px-4 py-2">
											<span
												className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${categoryBadge[entry.category] ?? "bg-gray-100 text-gray-700"}`}
											>
												{entry.category}
											</span>
										</td>
										<td className="px-4 py-2">
											<span
												className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
													entry.publishedAt
														? "bg-green-100 text-green-700"
														: "bg-gray-100 text-gray-600"
												}`}
											>
												{entry.publishedAt ? "published" : "draft"}
											</span>
										</td>
										<td className="px-4 py-2 text-gray-600">
											{entry.version ?? "—"}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{new Date(entry.createdAt).toLocaleDateString()}
										</td>
										<td className="px-4 py-2">
											<button
												type="button"
												disabled={deleting === entry.id}
												onClick={() => handleDelete(entry)}
												className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
											>
												Delete
											</button>
										</td>
									</tr>
								))}
								{data.data.length === 0 && (
									<tr>
										<td
											colSpan={6}
											className="px-4 py-8 text-center text-gray-500"
										>
											No changelog entries found.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between mt-4">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm text-gray-600">
							Page {data.pagination.page} of {data.pagination.totalPages || 1}
						</span>
						<button
							type="button"
							disabled={page >= data.pagination.totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</>
			)}
		</div>
	);
}
